import React from 'react'
import { useDropzone } from 'react-dropzone'
import { Box, Typography } from '@mui/material'
import * as pdfjsLib from 'pdfjs-dist'
import { useDispatch, useSelector } from 'react-redux'
import { addDrawingFile, updatePage } from '../../actions/files'
import { FileTag, FileType, LoadingPage, Page } from '../../models/File'
import { v4 as uuidv4 } from 'uuid'
import { uploadExtractFilesAsync } from '../../services/rulesService'
import { RootState } from '../../store'
import { AddFileIcon } from '../Svgs'
import { getFileExtension } from '../../utils/Common'
import DXFParser from 'dxf-parser'
import { drawDxfOnCanvas } from '../../utils/FileUtil'
import { pdfPagesNumber, pdfToImage } from '../../utils/PdfUtil'

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`

const FILE_TYPES = {
  PDF: 'application/pdf',
  IFC: 'application/x-ifc',
  IFC_ALT: 'application/ifc',
  DXF: 'application/dxf',
}

const ACCEPTED_FILE_TYPES = {
  [FILE_TYPES.PDF]: { [FILE_TYPES.PDF]: ['.pdf'] },
  [FILE_TYPES.IFC]: {
    [FILE_TYPES.IFC]: ['.ifc'],
    [FILE_TYPES.IFC_ALT]: ['.ifc'],
  },
  [FILE_TYPES.DXF]: { [FILE_TYPES.DXF]: ['.dxf'] },
}

interface UploadFileProps {
  fileTag?: FileTag
  removeHandler?: (fileId: string) => void
  cleanupFunctionsRef?: React.MutableRefObject<(() => void)[]>
}

const UploadFile: React.FC<UploadFileProps> = ({
  fileTag,
  cleanupFunctionsRef,
}) => {
  const dispatch = useDispatch<any>()
  const files = useSelector((state: RootState) => state.drawings.files)
  const filesType = FileType.Pdf || null

  const FILE_TYPE_HANDLERS = {
    [FileType.Pdf]: handlePdfFiles,
    [FileType.Ifc]: handleIfcFiles,
    [FileType.Dxf]: handleDxfFiles,
  }

  const getAcceptType = (fileType: FileType | null) => {
    switch (fileType) {
      case FileType.Pdf:
        return ACCEPTED_FILE_TYPES[FILE_TYPES.PDF]
      case FileType.Ifc:
        return ACCEPTED_FILE_TYPES[FILE_TYPES.IFC]
      case FileType.Dxf:
        return ACCEPTED_FILE_TYPES[FILE_TYPES.DXF]
      default:
        return ACCEPTED_FILE_TYPES[FILE_TYPES.PDF]
    }
  }

  const isUploadDisabled = () => {
    return !!files.find((file) => file.tag === fileTag)
  }

  async function handlePdfFiles(acceptedFiles: File[], dispatch: any) {
    const pdfFiles = acceptedFiles.filter(
      (file) => file.type === FILE_TYPES.PDF,
    )
    const files: { id: string; source: File }[] = []

    const loadFile = (file: File) => {
      const fileReader = new FileReader()
      const pages: LoadingPage[] = []
      const fileId = uuidv4()
      files.push({ id: fileId, source: file })

      fileReader.onload = async () => {
        if (fileReader.result) {
          try {
            const pdfData = fileReader.result as string
            const numPages = await pdfPagesNumber(pdfData)
            for (let i = 1; i < numPages + 1; i++) {
              const pageId = uuidv4()
              pages.push({
                id: pageId,
                number: i,
                image: '',
                isLoading: true,
                filename: file.name,
              })
            }

            dispatch(
              addDrawingFile({
                file: {
                  id: fileId,
                  name: file.name,
                  pages,
                },
              }),
            )

            pdfToImage(fileId, pages, pdfData, dispatch, (cleanup) => {
              if (cleanupFunctionsRef) {
                cleanupFunctionsRef.current.push(cleanup)
              }
            })
          } catch (error) {
            console.error(error)
          }
        }
      }

      fileReader.readAsArrayBuffer(file)
    }

    pdfFiles.map(loadFile)
    await dispatch(uploadExtractFilesAsync({ files }))
  }

  async function handleIfcFiles(acceptedFiles: File[], dispatch: any) {
    const ifcFiles = acceptedFiles.filter(
      (file) =>
        file.type === FILE_TYPES.IFC ||
        file.type === FILE_TYPES.IFC_ALT ||
        getFileExtension(file.name) === 'ifc',
    )

    if (!ifcFiles.length) {
      return
    }

    const fileId = uuidv4()
    dispatch(
      addDrawingFile({
        file: {
          id: fileId,
          name: ifcFiles[0].name,
          pages: [],
        },
      }),
    )

    await dispatch(
      uploadExtractFilesAsync({ files: [{ id: fileId, source: ifcFiles[0] }] }),
    )
  }

  async function handleDxfFiles(acceptedFiles: File[], dispatch: any) {
    const dxfFiles = acceptedFiles.filter(
      (file) =>
        file.type === FILE_TYPES.DXF || getFileExtension(file.name) === 'dxf',
    )

    if (!dxfFiles.length) {
      return
    }

    const fileId = uuidv4()
    const pageId = uuidv4()
    const file = {
      id: fileId,
      name: dxfFiles[0].name,
      pages: [
        {
          id: pageId,
          number: 1,
          image: '',
          isLoading: true,
          filename: dxfFiles[0].name,
        },
      ],
      tag: fileTag,
    }
    dispatch(addDrawingFile({ file }))

    const fileReader = new FileReader()
    fileReader.onload = () => {
      if (fileReader.result) {
        try {
          const parser = new DXFParser()
          const dxf = parser.parseSync(fileReader.result as string)
          if (!!dxf) {
            const canvas = document.createElement('canvas')
            drawDxfOnCanvas(dxf, canvas)
            const imageUrl = canvas.toDataURL('image/png')
            dispatch(
              updatePage({
                fileId: file.id,
                page: {
                  id: pageId,
                  number: 1,
                  image: imageUrl,
                  isLoading: false,
                  filename: dxfFiles[0].name,
                },
              }),
            )
          }
        } catch (error) {
          console.error('Error parsing DXF file:', error)
        }
      }
    }
    fileReader.readAsText(dxfFiles[0])

    await dispatch(
      uploadExtractFilesAsync({ files: [{ id: fileId, source: dxfFiles[0] }] }),
    )
  }

  const onDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      const handler = filesType ? FILE_TYPE_HANDLERS[filesType] : null
      if (handler) {
        await handler(acceptedFiles, dispatch)
      } else {
        console.error('Unsupported file type:', filesType)
      }
    },
    [dispatch, filesType],
  )

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: getAcceptType(filesType),
  })

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        border: `1px solid #3F69FF`,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        {...getRootProps()}
        sx={{
          display: 'flex',
          width: '100%',
          flex: 1,
          cursor: isUploadDisabled() ? 'default' : 'pointer',
          pointerEvents: isUploadDisabled() ? 'none' : 'all',
        }}
      >
        <input {...getInputProps()} />
        <Box
          sx={{
            height: 'calc(100% - 20px)',
            width: 'calc(100% - 20px)',
            borderRadius: 2,
            margin: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: isUploadDisabled()
              ? '#F5F5F5'
              : isDragActive
                ? '#D0E8FF'
                : isDragAccept
                  ? '#E0FFD0'
                  : isDragReject
                    ? '#FFD0D0'
                    : '#F5F5F5',
            transition: 'background-color 0.3s ease-in-out',
            '&:hover': {
              backgroundColor: isUploadDisabled() ? '#F5F5F5' : '#D0E8FF',
            },
          }}
        >
          <AddFileIcon color={isUploadDisabled() ? '#919191' : '#3F69FF'} />

          <Box sx={{ display: 'flex', gap: '5px' }}>
            <Typography
              sx={{
                color: isUploadDisabled() ? '#919191' : '#3F69FF',

                fontSize: '10px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: 'normal',
              }}
            >
              Drag and drop or
            </Typography>
            <Typography
              sx={{
                color: isUploadDisabled() ? '#919191' : '#3F69FF',

                fontSize: '10px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: 'normal',
                textDecoration: 'underline',
              }}
            >
              use uploader
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default UploadFile
